<!-- eslint-disable -->
<template>
  <div class="crm-listview">
    <div class="crm-listview__table-wrap">
      <table class="crm-listview__table">
        <!-- Header -->
        <thead>
          <tr class="crm-listview__header-row">
            <th v-if="bulkMode" class="crm-listview__th crm-listview__th--check">
              <label class="crm-listview__select-all">
                <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" />
                <span class="crm-listview__custom-check"></span>
              </label>
            </th>
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              class="crm-listview__th"
              :class="[
                'crm-listview__th--' + col.key,
                { 'crm-listview__th--sortable': col.sortable },
                { 'crm-listview__th--sorted': sortColumn === col.key },
              ]"
              @click="col.sortable ? handleSort(col.key) : null"
            >
              <span class="crm-listview__th-content">
                {{ col.label }}
                <span v-if="col.sortable" class="crm-listview__sort-arrow">
                  <feather-icon
                    :icon="sortColumn === col.key && sortDirection === 'desc' ? 'ChevronDownIcon' : 'ChevronUpIcon'"
                    size="13"
                  />
                </span>
              </span>
            </th>
            <th class="crm-listview__th crm-listview__th--actions">Actions</th>
          </tr>
        </thead>

        <!-- Body -->
        <tbody v-if="paginatedCards.length > 0">
          <tr
            v-for="card in paginatedCards"
            :key="card.id"
            class="crm-listview__row"
            :class="{ 'crm-listview__row--selected': card.selected }"
            @click="$emit('card-click', card)"
          >
            <!-- Checkbox -->
            <td v-if="bulkMode" class="crm-listview__td crm-listview__td--check" @click.stop>
              <label class="crm-listview__row-check">
                <input type="checkbox" :checked="card.selected" @change="$emit('card-select', card)" />
                <span class="crm-listview__custom-check"></span>
              </label>
            </td>

            <!-- ID -->
            <td class="crm-listview__td crm-listview__td--id">
              <span class="crm-listview__id">{{ card.id }}</span>
            </td>

            <!-- Title -->
            <td class="crm-listview__td crm-listview__td--title">
              <span class="crm-listview__title-text">{{ card.title }}</span>
            </td>

            <!-- Client -->
            <td class="crm-listview__td crm-listview__td--client">
              <span class="crm-listview__client-text">{{ card.client }}</span>
            </td>

            <!--
              Last Status — Karim, 3 Sep: "5 client, companies and
              opportunity in pipeline." Raw Jeem/Saber text is the tooltip;
              the badge is OUR translated version (same pattern as
              KanbanCard.vue).
            -->
            <td class="crm-listview__td crm-listview__td--last-status">
              <span
                v-if="card.lastRequestStatusLabel"
                class="crm-listview__priority-badge"
                :class="'crm-listview__priority-badge--' + lastStatusVariant(card)"
                :title="`Jeem/Saber: ${card.lastRequestStatus}`"
              >{{ card.lastRequestStatusLabel }}</span>
              <span v-else class="crm-listview__muted">—</span>
            </td>

            <!-- Stage -->
            <td class="crm-listview__td crm-listview__td--stage" @click.stop>
              <div class="crm-listview__stage-cell">
                <span
                  class="crm-listview__stage-badge"
                  :style="{ background: getStageBg(card.stage), color: getStageColor(card.stage) }"
                >
                  {{ getStageName(card.stage) }}
                </span>
                <select
                  class="crm-listview__stage-select"
                  :value="card.stage"
                  @change="$emit('stage-change', { card, newStage: $event.target.value })"
                >
                  <option v-for="s in stages" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
              </div>
            </td>

            <!-- Priority / Value -->
            <td class="crm-listview__td crm-listview__td--priority">
              <template v-if="activePipeline === 'tickets'">
                <span class="crm-listview__priority-badge" :class="'crm-listview__priority-badge--' + (card.priority || 'Medium').toLowerCase()">
                  {{ card.priority || 'Medium' }}
                </span>
              </template>
              <template v-else>
                <span class="crm-listview__value">{{ card.currency || 'AED' }} {{ formatValue(card.value) }}</span>
              </template>
            </td>

            <!-- Labels -->
            <td class="crm-listview__td crm-listview__td--labels">
              <div class="crm-listview__labels">
                <span
                  v-for="(label, idx) in getVisibleLabels(card.labels)"
                  :key="idx"
                  class="crm-listview__label-dot"
                  :style="{ background: label.color }"
                  :title="label.name"
                ></span>
                <span v-if="card.labels && card.labels.length > 3" class="crm-listview__label-more">
                  +{{ card.labels.length - 3 }}
                </span>
              </div>
            </td>

            <!-- Due Date -->
            <td class="crm-listview__td crm-listview__td--due">
              <span
                v-if="card.dueDate"
                class="crm-listview__due"
                :class="{ 'crm-listview__due--overdue': isOverdue(card.dueDate) }"
              >
                <feather-icon icon="ClockIcon" size="12" />
                {{ formatDate(card.dueDate) }}
              </span>
              <span v-else class="crm-listview__due crm-listview__due--none">—</span>
            </td>

            <!-- Assignee -->
            <td class="crm-listview__td crm-listview__td--assignee">
              <span v-if="card.owner" class="crm-listview__assignee-avatar" :title="card.owner">
                {{ getAssigneeInitial(card) }}
              </span>
              <span v-else class="crm-listview__assignee-empty" title="Unassigned">—</span>
            </td>

            <!-- Date info -->
            <td class="crm-listview__td crm-listview__td--date">
              <template v-if="activePipeline === 'renewals' && card.remainingDays !== undefined">
                <span
                  class="crm-listview__remaining"
                  :class="{
                    'crm-listview__remaining--warn': card.remainingDays <= 30 && card.remainingDays > 7,
                    'crm-listview__remaining--danger': card.remainingDays <= 7,
                  }"
                >
                  {{ card.remainingDays }}d left
                </span>
              </template>
              <template v-else>
                <span class="crm-listview__created">{{ formatDate(card.createdAt) }}</span>
              </template>
            </td>

            <!-- Actions -->
            <td class="crm-listview__td crm-listview__td--actions" @click.stop>
              <div class="crm-listview__action-btns">
                <button class="crm-listview__action-btn" title="View" @click="$emit('card-click', card)">
                  <feather-icon icon="EyeIcon" size="15" />
                </button>
                <button class="crm-listview__action-btn crm-listview__action-btn--danger" title="Delete" @click="$emit('card-delete', card)">
                  <feather-icon icon="Trash2Icon" size="15" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- Empty state -->
        <tbody v-else>
          <tr>
            <td :colspan="totalColspan" class="crm-listview__empty">
              <div class="crm-listview__empty-content">
                <feather-icon icon="InboxIcon" size="40" />
                <p>No records found</p>
                <span>Try adjusting your filters or search query</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="cards.length" class="crm-listview__pagination">
      <span class="crm-listview__pagination-info">
        Showing {{ paginationStart }}–{{ paginationEnd }} of {{ cards.length }}
      </span>
      <!-- Karim, 8 Sep: "the pagination page is showing 1 to 10, I need to
           show more if possible." -->
      <select v-model.number="perPage" class="crm-listview__page-size" @change="currentPage = 1">
        <option :value="10">10 / page</option>
        <option :value="25">25 / page</option>
        <option :value="50">50 / page</option>
        <option :value="100">100 / page</option>
        <option :value="250">250 / page</option>
      </select>
      <div v-if="totalPages > 1" class="crm-listview__pagination-btns">
        <button
          class="crm-listview__page-btn"
          :disabled="currentPage === 1"
          @click="currentPage = 1"
        >
          <feather-icon icon="ChevronsLeftIcon" size="14" />
        </button>
        <button
          class="crm-listview__page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          <feather-icon icon="ChevronLeftIcon" size="14" />
        </button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="crm-listview__page-btn"
          :class="{ 'crm-listview__page-btn--active': page === currentPage }"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
        <button
          class="crm-listview__page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          <feather-icon icon="ChevronRightIcon" size="14" />
        </button>
        <button
          class="crm-listview__page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage = totalPages"
        >
          <feather-icon icon="ChevronsRightIcon" size="14" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment'

export default {
  name: 'ListView',

  props: {
    cards: {
      type: Array,
      required: true,
    },
    stages: {
      type: Array,
      required: true,
    },
    activePipeline: {
      type: String,
      required: true,
    },
    boardSettings: {
      type: Object,
      required: true,
    },
    bulkMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentPage: 1,
      perPage: 25,
      sortColumn: 'date',
      sortDirection: 'asc',
    }
  },

  computed: {
    visibleColumns() {
      const cols = [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'title', label: 'Name / Title', sortable: true },
        { key: 'client', label: 'Client / Facility', sortable: true },
        // Karim, 3 Sep: "5 client, companies and opportunity in pipeline."
        { key: 'lastStatus', label: 'Last Status', sortable: false },
        { key: 'stage', label: 'Stage', sortable: false },
        {
          key: 'priority',
          label: this.activePipeline === 'tickets' ? 'Priority' : 'Value',
          sortable: true,
        },
        { key: 'labels', label: 'Labels', sortable: false },
        { key: 'due', label: 'Due Date', sortable: true },
        { key: 'assignee', label: 'Assignee', sortable: true },
        { key: 'date', label: this.activePipeline === 'renewals' ? 'Remaining' : 'Date', sortable: true },
      ]
      return cols
    },

    totalColspan() {
      return this.visibleColumns.length + (this.bulkMode ? 2 : 1) // +1 for actions, +1 for checkbox
    },

    sortedCards() {
      const arr = [...this.cards]
      const dir = this.sortDirection === 'asc' ? 1 : -1

      arr.sort((a, b) => {
        switch (this.sortColumn) {
          case 'id':
            return a.id.localeCompare(b.id) * dir
          case 'title':
            return (a.title || '').localeCompare(b.title || '') * dir
          case 'client':
            return (a.client || '').localeCompare(b.client || '') * dir
          case 'priority':
            if (this.activePipeline === 'tickets') {
              const pMap = { High: 3, Medium: 2, Low: 1 }
              return ((pMap[a.priority] || 2) - (pMap[b.priority] || 2)) * dir
            }
            return ((a.value || 0) - (b.value || 0)) * dir
          case 'due':
            return this.compareDates(a.dueDate, b.dueDate) * dir
          case 'assignee':
            return (this.getAssigneeName(a)).localeCompare(this.getAssigneeName(b)) * dir
          case 'date':
            if (this.activePipeline === 'renewals') {
              return ((a.remainingDays || 0) - (b.remainingDays || 0)) * dir
            }
            return this.compareDates(a.createdAt, b.createdAt) * dir
          default:
            return 0
        }
      })

      return arr
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.sortedCards.length / this.perPage))
    },

    paginatedCards() {
      const start = (this.currentPage - 1) * this.perPage
      return this.sortedCards.slice(start, start + this.perPage)
    },

    paginationStart() {
      return (this.currentPage - 1) * this.perPage + 1
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.cards.length)
    },

    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)

      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4)
        else start = Math.max(1, end - 4)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },

    allSelected() {
      return this.paginatedCards.length > 0 && this.paginatedCards.every((c) => c.selected)
    },
  },

  watch: {
    cards() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = Math.max(1, this.totalPages)
      }
    },
  },

  methods: {
    lastStatusVariant(card) {
      const map = { issued: 'issued', rejected: 'rejected', in_progress: 'in-progress' }
      return map[card.lastRequestStatusNormalized] || 'unknown'
    },
    handleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
      this.$emit('sort-change', { column: this.sortColumn, direction: this.sortDirection })
    },

    toggleSelectAll() {
      const allSel = this.allSelected
      this.paginatedCards.forEach((card) => {
        if (allSel && card.selected) {
          this.$emit('card-select', card)
        } else if (!allSel && !card.selected) {
          this.$emit('card-select', card)
        }
      })
    },

    getStageName(stageId) {
      const s = this.stages.find((st) => st.id === stageId)
      return s ? s.name : stageId
    },

    getStageColor(stageId) {
      const s = this.stages.find((st) => st.id === stageId)
      return s ? s.color : '#64748b'
    },

    getStageBg(stageId) {
      const color = this.getStageColor(stageId)
      return color + '18'
    },

    getVisibleLabels(labels) {
      if (!labels) return []
      return labels.slice(0, 3)
    },

    // MID-CRM P1.3 — `card.assignees` dropped: no endpoint returns it and there is
    // no assignees table. A card has exactly one owner.
    getAssigneeInitial(card) {
      return (card.owner || '').charAt(0).toUpperCase()
    },

    getAssigneeName(card) {
      return card.owner || ''
    },

    formatValue(val) {
      if (!val && val !== 0) return '—'
      // Currency comes from the row, not a hardcoded assumption — see KanbanCard.
      return Number(val).toLocaleString()
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      return moment(dateStr).format('MMM D, YYYY')
    },

    isOverdue(dateStr) {
      if (!dateStr) return false
      return moment(dateStr).isBefore(moment(), 'day')
    },

    compareDates(a, b) {
      const da = a ? new Date(a).getTime() : 0
      const db = b ? new Date(b).getTime() : 0
      return da - db
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #6366f1;
$primary-dark: #4f46e5;
$success: #10b981;
$warning: #f59e0b;
$danger: #f43f5e;
$slate-50: #f8fafc;
$slate-100: #f1f5f9;
$slate-200: #e2e8f0;
$slate-300: #cbd5e1;
$slate-400: #94a3b8;
$slate-500: #64748b;
$slate-600: #475569;
$slate-700: #334155;
$slate-900: #0f172a;

// Karim, 3 Sep: OUR translated status (raw Jeem/Saber text stays as the
// tooltip). "Terminated" -> issued/green, not danger/red — see
// CrmAccountActivityService::normaliseStatus(). This component's existing
// `.crm-listview__priority-badge` classes had no CSS anywhere in the repo
// (checked 3 Sep) — scoping this fresh here rather than perpetuating that.
.crm-listview__priority-badge--issued { background: rgba(40, 199, 111, .12); color: #1f9254; }
.crm-listview__priority-badge--rejected { background: rgba(234, 84, 85, .1); color: #ea5455; }
.crm-listview__priority-badge--in-progress { background: rgba(255, 159, 67, .12); color: #b9690e; }
.crm-listview__priority-badge--unknown { background: rgba(115, 103, 240, .08); color: #7367f0; }
.crm-listview__priority-badge--issued,
.crm-listview__priority-badge--rejected,
.crm-listview__priority-badge--in-progress,
.crm-listview__priority-badge--unknown {
  display: inline-flex;
  align-items: center;
  padding: .2rem .55rem;
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 700;
  white-space: nowrap;
}
.crm-listview__muted {
  color: #b3b1c7;
  font-size: .78rem;
}

.crm-listview {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 20px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  &__table-wrap {
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: $slate-300;
      border-radius: 3px;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Outfit', 'Inter', sans-serif;
  }

  /* ── Header ── */
  &__header-row {
    background: $slate-100;
  }

  &__th {
    padding: 12px 14px;
    text-align: left;
    font-size: 11.5px;
    font-weight: 600;
    color: $slate-500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    border-bottom: 1px solid $slate-200;
    user-select: none;

    &--check {
      width: 40px;
      text-align: center;
      padding-left: 16px;
    }

    &--sortable {
      cursor: pointer;
      transition: color 0.15s ease;

      &:hover {
        color: $slate-700;
      }
    }

    &--sorted {
      color: $primary;
    }

    &--id {
      width: 90px;
    }

    &--title {
      min-width: 200px;
    }

    &--actions {
      width: 100px;
      text-align: center;
    }
  }

  &__th-content {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__sort-arrow {
    display: inline-flex;
    opacity: 0.4;
    transition: opacity 0.15s ease;

    .crm-listview__th--sorted & {
      opacity: 1;
      color: $primary;
    }
  }

  /* ── Rows ── */
  &__row {
    cursor: pointer;
    transition: background 0.15s ease;
    border-bottom: 1px solid $slate-100;

    &:hover {
      background: $slate-50;
    }

    &--selected {
      background: rgba($primary, 0.04);

      &:hover {
        background: rgba($primary, 0.07);
      }
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__td {
    padding: 13px 14px;
    font-size: 13px;
    color: $slate-700;
    vertical-align: middle;

    &--check {
      text-align: center;
      padding-left: 16px;
    }

    &--id {
      font-family: 'JetBrains Mono', 'Fira Code', monospace;
      font-size: 12px;
      color: $slate-500;
    }

    &--title {
      font-weight: 700;
      color: $slate-900;
      max-width: 260px;
    }

    &--client {
      color: $slate-600;
      max-width: 180px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &--actions {
      text-align: center;
    }
  }

  /* ── Custom checkbox ── */
  &__select-all,
  &__row-check {
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    input[type="checkbox"] {
      display: none;
    }
  }

  &__custom-check {
    width: 17px;
    height: 17px;
    border: 1.5px solid $slate-300;
    border-radius: 4px;
    position: relative;
    transition: all 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 5px;
      width: 5px;
      height: 8px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg) scale(0);
      transition: transform 0.15s ease;
    }
  }

  input:checked + &__custom-check {
    background: $primary;
    border-color: $primary;

    &::after {
      transform: rotate(45deg) scale(1);
    }
  }

  /* ── ID ── */
  &__id {
    padding: 3px 8px;
    background: $slate-100;
    border-radius: 5px;
    font-weight: 500;
  }

  /* ── Title ── */
  &__title-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ── Client ── */
  &__client-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ── Stage ── */
  &__stage-cell {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__stage-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
  }

  &__stage-select {
    width: 20px;
    height: 20px;
    opacity: 0;
    cursor: pointer;
    position: relative;
    margin-left: -10px;

    &:hover {
      opacity: 0.5;
    }
  }

  /* ── Priority badge ── */
  &__priority-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;

    &--high {
      background: rgba($danger, 0.1);
      color: $danger;
    }

    &--medium {
      background: rgba($warning, 0.1);
      color: darken($warning, 10%);
    }

    &--low {
      background: rgba($success, 0.1);
      color: $success;
    }
  }

  /* ── Value ── */
  &__value {
    font-weight: 700;
    color: $success;
    white-space: nowrap;
  }

  /* ── Labels ── */
  &__labels {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__label-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: transform 0.15s ease;

    &:hover {
      transform: scale(1.3);
    }
  }

  &__label-more {
    font-size: 11px;
    color: $slate-400;
    font-weight: 500;
    margin-left: 2px;
  }

  /* ── Due date ── */
  &__due {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12.5px;
    color: $slate-500;
    white-space: nowrap;

    &--overdue {
      color: $danger;
      font-weight: 600;
    }

    &--none {
      color: $slate-300;
    }
  }

  /* ── Assignee ── */
  &__assignee-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
  }

  &__assignee-empty {
    color: $slate-300;
  }

  /* ── Remaining days ── */
  &__remaining {
    font-size: 12.5px;
    font-weight: 600;
    color: $success;

    &--warn {
      color: $warning;
    }

    &--danger {
      color: $danger;
    }
  }

  &__created {
    font-size: 12.5px;
    color: $slate-500;
    white-space: nowrap;
  }

  /* ── Action buttons ── */
  &__action-btns {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  &__action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: 7px;
    color: $slate-400;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: $slate-100;
      color: $slate-700;
    }

    &--danger:hover {
      background: rgba($danger, 0.08);
      color: $danger;
    }
  }

  /* ── Empty state ── */
  &__empty {
    padding: 60px 20px;
    text-align: center;
  }

  &__empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: $slate-400;

    p {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: $slate-500;
    }

    span {
      font-size: 13px;
      color: $slate-400;
    }
  }

  /* ── Pagination ── */
  &__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-top: 1px solid $slate-200;
    background: $slate-50;
  }

  &__pagination-info {
    font-size: 13px;
    color: $slate-500;
    font-weight: 500;
  }

  &__pagination-btns {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__page-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    border: 1px solid $slate-200;
    background: #fff;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 500;
    color: $slate-600;
    cursor: pointer;
    font-family: 'Outfit', 'Inter', sans-serif;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      border-color: $primary;
      color: $primary;
      background: rgba($primary, 0.04);
    }

    &--active {
      background: $primary !important;
      border-color: $primary !important;
      color: #fff !important;
      box-shadow: 0 2px 8px rgba($primary, 0.25);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }
}

.crm-listview__page-size {
  border: 1.5px solid $slate-200; border-radius: 8px; padding: 4px 8px;
  font-size: 12.5px; color: $slate-600; background: #fff; margin-left: auto;
}
</style>
